import { SubmitHandler, useFormContext, Controller } from "react-hook-form";
import {
  useGetPrioritiesQuery,
  useGetCategoriesQuery,
  useGetSubcategoriesQuery,
  useGetClientsQuery,
  useGetUpdateReasonsQuery,
  useGetAgentsQuery,
  useGetStatusesQuery,
  useLazyGetClientsQuery,
} from "../api/ticketApi/selectInputApis";
import { Button, Label, Modal, Textarea } from "flowbite-react";
import CustomTextInput from "../components/CustomTextInput";
import CustomSelect from "../components/CustomSelect";
import SearchWithDropdown from "../components/SearchWithDropdown";
import { useEffect, useMemo, useState } from "react";
import { PaginatedResponse, Subcategory, Ticket } from "@/sharedTypes";

interface TicketFormDialogProps {
  open: boolean;
  onClose: () => void;
  mode: "create" | "edit";
  onSubmit: (data: Ticket) => void;
  isLoading: boolean;
}

function TicketFormDialog({
  open,
  onClose,
  mode,
  onSubmit,
  isLoading,
}: TicketFormDialogProps) {
  const methods = useFormContext<Ticket>();
  const { handleSubmit, watch, setValue, formState, control, clearErrors } =
    methods;

  const { data: priorities } = useGetPrioritiesQuery();
  const { data: categories } = useGetCategoriesQuery();
  const { data: updateReasons } = useGetUpdateReasonsQuery();
  const { data: agents } = useGetAgentsQuery();
  const { data: statuses } = useGetStatusesQuery();

  const [subcategories, setSubcategories] =
    useState<PaginatedResponse<Subcategory>>();
  const selectedCategoryId = watch("last_category_id");
  const lastAssignedAgentId = watch("last_assigned_agent_id");
  const clientId = watch("client_id");

  const [assignTo, setAssignTo] = useState<"self" | "agent">("self");

  const { data: subcategoriesData } = useGetSubcategoriesQuery(
    selectedCategoryId as number,
    { skip: !selectedCategoryId }
  );

  const [clientSearchRecord, setClientSearchRecord] = useState<
    Record<string, any>
  >({});
  const [searchTimeout, setSearchTimeout] = useState<NodeJS.Timeout | null>(
    null
  );

  const { data: clients } = useGetClientsQuery(clientSearchRecord);
  const [fetchClient] = useLazyGetClientsQuery();

  const [clientOptions, setClientOptions] = useState(
    clients?.items?.map((client: any) => ({
      value: client.client_id?.toString(),
      label: client.client_username,
    })) || []
  );

  useEffect(() => {
    setClientOptions(
      clients?.items?.map((client: any) => ({
        value: client.client_id?.toString(),
        label: client.client_username,
      })) || []
    );
  }, [clients]);

  useEffect(() => {
    if (subcategoriesData) {
      setSubcategories(subcategoriesData);

      if (mode === "edit") {
        const currentSubcategoryId = methods.getValues("last_subcategory_id");
        const isSubcategoryValid = subcategoriesData.items.some(
          (subcategory: any) =>
            subcategory.subcategory_id === currentSubcategoryId
        );

        if (!isSubcategoryValid) {
          setValue("last_subcategory_id", null);
        }
      }
    }
  }, [subcategoriesData, mode, setValue, methods]);

  useEffect(() => {
    if (mode === "edit") {
      const savedUserData = JSON.parse(localStorage.getItem("userData") || "{}");
      const loggedInAgentId = savedUserData?.agent_id;

      if (lastAssignedAgentId && lastAssignedAgentId !== loggedInAgentId) {
        setAssignTo("agent");
      } else {
        setAssignTo("self");
      }
    }
  }, [mode, lastAssignedAgentId]);

  useEffect(() => {
    if (mode === "edit" && clientId) {
      const isClientInOptions = clientOptions.some(
        (option: any) => option.value === clientId.toString()
      );

      if (!isClientInOptions) {
        fetchClient({ client_id: clientId })
          .unwrap()
          .then((response) => {
            if (response?.items?.length > 0) {
              const client = response.items[0];
              const newOption = {
                value: client.client_id?.toString(),
                label: client.client_username,
              };
              setClientOptions((prev: any) => [...prev, newOption]);
              setValue("client_id", client.client_id?.toString());
            }
          })
          .catch(() => {
            console.error("Failed to fetch client");
          });
      }
    }
  }, [mode, clientId, clientOptions, fetchClient, setValue]);

  const handleClientSearch = (query: string) => {
    if (searchTimeout) clearTimeout(searchTimeout);
    const timeout = setTimeout(() => {
      setClientSearchRecord((prev) => ({
        ...prev,
        client_username: query,
        client_email: query,
      }));
    }, 500);
    setSearchTimeout(timeout);
  };

  // ----------------------------
  // Resolution note + status change guard
  // ----------------------------
  const resolvedStatusId = useMemo(() => {
    const list = statuses?.items ?? [];
    const match = list.find((s: any) => {
      const n = String(s?.status_name ?? "").toLowerCase();
      return n.includes("resolved") || n.includes("çözü") || n.includes("cozu");
    });
    return match?.status_id ? Number(match.status_id) : null;
  }, [statuses]);

  const watchedStatusId = watch("last_status_id");

  const [formInitialized, setFormInitialized] = useState(false);
  const [initialStatusId, setInitialStatusId] = useState<number | null>(null);

  const [showStatusChangeWarning, setShowStatusChangeWarning] = useState(false);
  const [pendingStatusChange, setPendingStatusChange] = useState<number | null>(
    null
  );

  useEffect(() => {
    if (mode === "edit" && open && !formInitialized) {
      const currentStatus = methods.getValues("last_status_id");
      setInitialStatusId(
        currentStatus === undefined || currentStatus === null
          ? null
          : Number(currentStatus)
      );
      setFormInitialized(true);
    } else if (!open) {
      setFormInitialized(false);
      setInitialStatusId(null);
      setShowStatusChangeWarning(false);
      setPendingStatusChange(null);
    }
  }, [mode, open, formInitialized, methods]);

  const handleStatusChange = (newStatusId: number) => {
    // Resolved ID bilinmiyorsa, sadece set et (warning/note mantığını zorlamayalım)
    if (!resolvedStatusId) {
      setValue("last_status_id", newStatusId);
      return;
    }

    const currentStatus = Number(methods.getValues("last_status_id") ?? 0);
    const wasResolved = currentStatus === resolvedStatusId;
    const isChangingFromResolved = wasResolved && newStatusId !== resolvedStatusId;

    if (isChangingFromResolved) {
      setPendingStatusChange(newStatusId);
      setShowStatusChangeWarning(true);
      return;
    }

    setValue("last_status_id", newStatusId);

    // Resolved değilse resolution_note'u temizle (opsiyonel ama temiz davranış)
    if (newStatusId !== resolvedStatusId) {
      setValue("resolution_note" as any, null as any);
      clearErrors("resolution_note" as any);
    }
  };

  const confirmStatusChange = () => {
    if (pendingStatusChange !== null) {
      setValue("last_status_id", pendingStatusChange);
      if (resolvedStatusId && pendingStatusChange !== resolvedStatusId) {
        setValue("resolution_note" as any, null as any);
        clearErrors("resolution_note" as any);
      }
    }
    setShowStatusChangeWarning(false);
    setPendingStatusChange(null);
  };

  const cancelStatusChange = () => {
    setShowStatusChangeWarning(false);
    setPendingStatusChange(null);
  };

  const handleFormSubmit: SubmitHandler<Ticket> = async (data) => {
    const savedUserData = JSON.parse(localStorage.getItem("userData") || "{}");
    const agentId = savedUserData?.agent_id || 0;

    // Eğer resolved değilse resolution_note'u göndermeyelim (backend strict ise)
    const shouldSendResolutionNote =
      resolvedStatusId &&
      Number(data?.last_status_id ?? 0) === resolvedStatusId &&
      initialStatusId !== resolvedStatusId;

    const payload: Ticket = {
      ...data,
      last_category_id: parseInt(data?.last_category_id?.toString() || ""),
      last_assigned_agent_id:
        assignTo === "self" ? agentId : data.last_assigned_agent_id,
      ...(shouldSendResolutionNote ? {} : { resolution_note: undefined as any }),
    };

    onSubmit(payload);
  };

  return (
    <>
      <Modal show={open} onClose={onClose}>
        <Modal.Header>
          {mode === "create" ? "Create New Ticket" : "Edit Ticket"}
        </Modal.Header>
        <Modal.Body>
          <div className="max-h-[80vh] overflow-y-auto">
            <form onSubmit={handleSubmit(handleFormSubmit)}>
              {mode === "edit" && (
                <Controller
                  name="last_status_id"
                  control={control}
                  rules={{ required: "Status is required" }}
                  render={({ field }) => (
                    <CustomSelect
                      id="status_id"
                      label="Status"
                      options={statuses?.items?.map((status: any) => ({
                        value: status.status_id?.toString(),
                        label: status.status_name,
                      }))}
                      value={field.value?.toString() ?? ""}
                      onChange={(e: any) => {
                        const v = Number(e?.target?.value);
                        if (!Number.isNaN(v)) {
                          handleStatusChange(v);
                          clearErrors("last_status_id");
                        }
                      }}
                      error={formState.errors?.last_status_id?.message}
                    />
                  )}
                />
              )}

              {/* Resolution Note (sadece resolved'a geçişte) */}
              {mode === "edit" &&
                formInitialized &&
                resolvedStatusId &&
                Number(watchedStatusId ?? 0) === resolvedStatusId &&
                initialStatusId !== resolvedStatusId && (
                  <div className="mb-4">
                    <Label htmlFor="resolution_note">Resolution Note</Label>
                    <Controller
                      name={"resolution_note" as any}
                      control={control}
                      rules={{
                        required: "Resolution note is required when resolving a ticket",
                      }}
                      render={({ field }) => (
                        <Textarea
                          id="resolution_note"
                          placeholder="Enter resolution details..."
                          rows={4}
                          value={(field.value as any) ?? ""}
                          onChange={(e: any) => field.onChange(e.target.value)}
                        />
                      )}
                    />
                    {formState.errors?.resolution_note?.message && (
                      <p className="mt-1 text-sm text-red-600">
                        {String(formState.errors.resolution_note.message)}
                      </p>
                    )}
                  </div>
                )}

              <CustomTextInput
                id="ticket_title"
                label="Title"
                placeholder="Enter ticket title"
                {...methods.register("ticket_title", {
                  required: "Title is required",
                })}
                error={formState.errors.ticket_title?.message}
              />

              <CustomTextInput
                id="ticket_description"
                label="Description"
                placeholder="Enter ticket description"
                {...methods.register("ticket_description", {
                  required: "Description is required",
                })}
                error={formState.errors.ticket_description?.message}
              />

              <CustomSelect
                id="last_priority_id"
                label="Priority"
                options={priorities?.items?.map((priority: any) => ({
                  value: priority.priority_id?.toString(),
                  label: priority?.priority_name,
                }))}
                {...methods.register("last_priority_id", {
                  required: "Priority is required",
                  valueAsNumber: true,
                })}
                error={formState.errors?.last_priority_id?.message}
              />

              <CustomSelect
                id="last_category_id"
                label="Category"
                options={categories?.items?.map((category: any) => ({
                  value: category?.category_id?.toString(),
                  label: category.category_name,
                }))}
                {...methods.register("last_category_id", {
                  required: "Category is required",
                  valueAsNumber: true,
                })}
                onChange={(e) => {
                  setValue("last_category_id", parseInt(e.target.value));
                  setValue("last_subcategory_id", null);
                  clearErrors("last_category_id");
                }}
                error={formState.errors?.last_category_id?.message}
              />

              <CustomSelect
                id="last_subcategory_id"
                label="Subcategory"
                options={
                  subcategories?.items?.map((subcategory: any) => ({
                    value: subcategory?.subcategory_id?.toString(),
                    label: subcategory?.subcategory_name,
                  })) ?? []
                }
                {...methods.register("last_subcategory_id", {
                  valueAsNumber: true,
                })}
                error={formState.errors?.last_subcategory_id?.message}
              />

              <Controller
                name="client_id"
                control={control}
                rules={{ required: "Client is required" }}
                render={({ field }) => (
                  <SearchWithDropdown
                    label="Client"
                    options={clientOptions}
                    placeholder="Search or select a client"
                    value={field.value?.toString() || ""}
                    onSearch={handleClientSearch}
                    onChange={(selectedOption) => {
                      if (
                        selectedOption?.value &&
                        !isNaN(Number(selectedOption.value))
                      ) {
                        field.onChange(parseInt(selectedOption.value));
                        clearErrors("client_id");
                      } else {
                        field.onChange(null);
                      }
                    }}
                    errMsg={formState.errors?.client_id?.message}
                  />
                )}
              />

              {mode === "edit" && (
                <CustomSelect
                  id="update_reason_id"
                  label="Update Reason"
                  options={updateReasons?.items?.map((reason: any) => ({
                    value: reason.update_reason_id?.toString(),
                    label: reason.update_reason_description,
                  }))}
                  {...methods.register("update_reason_id", {
                    required: "Update reason is required",
                    valueAsNumber: true,
                  })}
                  error={formState.errors?.update_reason_id?.message}
                />
              )}

              <div className="mb-4">
                <Label htmlFor="assign_to">Assign To</Label>
                <div className="space-y-2">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      value="self"
                      checked={assignTo === "self"}
                      onChange={() => setAssignTo("self")}
                      className="mr-2"
                    />
                    Assign to self
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      value="agent"
                      checked={assignTo === "agent"}
                      onChange={() => setAssignTo("agent")}
                      className="mr-2"
                    />
                    Assign to agent
                  </label>
                </div>
              </div>

              {assignTo === "agent" && (
                <CustomSelect
                  id="assigned_agent_id"
                  label="Agent"
                  options={
                    agents?.items?.map((agent: any) => ({
                      value: agent.agent_id.toString(),
                      label: agent.agent_username,
                    })) ?? []
                  }
                  {...methods.register("last_assigned_agent_id", {
                    required: "Agent selection is required when assigning to agent",
                    valueAsNumber: true,
                  })}
                  error={formState.errors.last_assigned_agent_id?.message}
                />
              )}

              <Button type="submit" className="w-full" disabled={isLoading}>
                {isLoading
                  ? mode === "create"
                    ? "Creating..."
                    : "Updating..."
                  : mode === "create"
                    ? "Create Ticket"
                    : "Update Ticket"}
              </Button>
            </form>
          </div>
        </Modal.Body>
      </Modal>

      <Modal show={showStatusChangeWarning} onClose={cancelStatusChange}>
        <Modal.Header>Change Resolved Status?</Modal.Header>
        <Modal.Body>
          <p className="text-sm text-gray-700">
            This ticket is currently resolved. Changing the status will mark the
            current resolution as &quot;old resolution&quot;. Do you want to continue?
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button color="gray" onClick={cancelStatusChange}>
            Cancel
          </Button>
          <Button onClick={confirmStatusChange}>Continue</Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default TicketFormDialog;
